import { animate, style, transition, trigger } from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  Output,
  EventEmitter,
} from '@angular/core';
import { loadImage } from './img-box.helper';
import { ImageAnnotation, ImageBoxEditorData, Vertex } from './typings';
import Konva from 'konva';
const DEFAULT_ACHOR_SIZE = 5;
const DEFAULT_NEW_BPOLY_VERTEX = [
  { x: 0, y: 0 },
  { x: 100, y: 0 },
  { x: 100, y: 80 },
  { x: 0, y: 80 },
] as Vertex[];
interface ImageBoxAnnotation {
  rect: Konva.Rect;
  transformer: Konva.Transformer;
  selectionRect: Konva.Rect;
  annotation: ImageAnnotation;
}

@Component({
  selector: 'ngx-image-box-editor',
  templateUrl: './image-box-editor.component.html',
  styleUrls: ['./image-box-editor.component.scss'],
  animations: [
    trigger('inOutAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('0.5s ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('0.5s ease-in', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class ImageBoxEditorComponent implements OnInit, AfterViewInit {
  private _data: ImageBoxEditorData;
  private _annotations: ImageAnnotation[];
  tableBounds;
  private _isMenuOpened: boolean = false;

  xaxisLine: any;
  xaxisSplitter = false;
  xaxisLineClick = false;
  yaxisSplitter = false;
  yaxisLine: any;
  yaxisLineClick = false;
  @ViewChild('mainContainer') mainContainer: ElementRef;
  @ViewChild('buttons', { static: false }) buttons: ElementRef;
  @Output() response: EventEmitter<any> = new EventEmitter();
  @ViewChild('annotationTextInput') annotationTextInput: ElementRef;
  editorstatus: boolean;
  @Input('data') set data(input: ImageBoxEditorData) {
    this._annotations = input?.annotations;
    this.tableBounds = input?.tableBounds;
    this.imgSrc = input?.imageSrc;
    this.updatedImageSrc = input?.updatedImageSrc;
    this._data = input;
    this.showWordBlocks = input?.showWordBlocks;
    this.errors = input?.errors;
    this.drawImgAnnotations(input?.fixSuggestions);
  }
  get data() {
    return this._data;
  }
  get annotations() {
    return this._annotations;
  }
  set annotations(input: ImageAnnotation[]) {
    if (this._data) this._data.annotations = input;
    this._annotations = input;
  }
  get isMenuOpened() {
    return this._isMenuOpened;
  }
  set isMenuOpened(input: boolean) {
    this._isMenuOpened = input;
    setTimeout(() => {
      const tableEl = document.getElementById('table-box-editor');
      const konvaEl = document.getElementById('konva-stage-parent');

      const height = konvaEl.offsetHeight;
      tableEl.style.height = `${height}px`;
    }, 300);
  }
  // konva attributes
  stage: Konva.Stage;
  layer: Konva.Layer;
  image: Konva.Image;
  imgSrc: string;
  updatedImageSrc: any;
  showWordBlocks: boolean;

  imgBoxAnnotations: ImageBoxAnnotation[];
  sceneWidth: number;
  sceneHeight: number;
  selectedAnnotation: ImageAnnotation;

  showAnnotationPopover: boolean = false;
  showAnnotationCode: boolean = false;
  bottomScale;
  errors = [];

  constructor(private cdRef: ChangeDetectorRef) {}
  ngAfterViewInit(): void {}

  ngOnInit(): void {
    // initialise konva
    this.stage = new Konva.Stage({
      container: 'konva-container',
      width: 0,
      height: 0,
    });
    this.layer = new Konva.Layer();
    this.stage.add(this.layer);
    // adapt the stage on any window resize
    window.addEventListener('resize', () => {
      this.fitStageIntoParentContainer();
    });
  }

  closePopover() {
    this.showAnnotationPopover = false;
    this.selectedAnnotation = null;
    if (this.annotationTextInput) {
      this.annotationTextInput.nativeElement.value = '';
    }
  }

  fitStageIntoParentContainer() {
    const container = document.getElementById('konva-stage-parent');

    // now we need to fit stage into parent container
    const containerWidth = container.offsetWidth;

    // but we also make the full scene visible
    // so we need to scale all objects on canvas
    const scale = containerWidth / this.sceneWidth;

    this.stage.width(this.sceneWidth * scale);
    this.stage.height(this.sceneHeight * scale);
    this.stage.scale({ x: scale, y: scale });
  }

  // async drawLine(show: boolean, event: any, annotation: ImageAnnotation) {
  //   if (this.arrowObj) {
  //     this.arrowObj.clear();
  //   }
  //   if (show) {
  //     const imgBoxAnnotation = this.imgBoxAnnotations.find(
  //       (a) => a.annotation === annotation,
  //     );
  //     const { rect } = imgBoxAnnotation;
  //     const canvasEl = rect.getCanvas()._canvas;
  //     if (canvasEl) {
  //       const rect1 = event.target;
  //       const rect2 = canvasEl;
  //       this.arrowObj = arrowCreate({
  //         className: 'arrow',
  //         from: {
  //           node: rect1,
  //         },
  //         to: {
  //           direction: DIRECTION.BOTTOM,
  //           node: rect2,
  //         },
  //         head: {
  //           func: HEAD.DIAMOND,
  //           width: 20,
  //           height: 30,
  //         },
  //       });
  //       document.body.appendChild(this.arrowObj.node);
  //     }
  //   } else {
  //     this.arrowObj?.clear();
  //   }
  // }

  exportAnnotations() {
    const annotationStr = JSON.stringify(this.annotations);
    const downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(
      new Blob([annotationStr], { type: 'application/json' }),
    );
    const filename = 'exported' + '.json';
    if (filename) downloadLink.setAttribute('download', filename);
    document.body.appendChild(downloadLink);
    downloadLink.click();
  }

  deleteSplitLines () {
    this.xaxisSplitter = false;
    this.yaxisSplitter = false;
    if (this.xaxisLine) {
      this.xaxisLine.destroy();
    }
    if (this.yaxisLine) {
      this.yaxisLine.destroy();
    }
  }

  async drawImgAnnotations(fixSuggestions) {
    // load img
    // this.logger.debug('drawImgAnnotations imgSrc: ', this.imgSrc);
    this.deleteSplitLines();
    if (!this.imgSrc && !this.updatedImageSrc) return;

    const img = this.updatedImageSrc ? await loadImage(this.updatedImageSrc) :  await loadImage(this.imgSrc);
    // draw img
    this.sceneHeight = img.height;
    this.sceneWidth = img.width;
    this.stage.size({ width: this.sceneWidth, height: this.sceneHeight });
    // render image
    this.image = new Konva.Image({
      x: 0,
      y: 0,
      image: img,
      width: img.width,
      height: img.height,
    });
    if (this.updatedImageSrc) {
      this.layer.children = [];
    }
    this.layer.add(this.image);

    // draw boxes
    this.imgBoxAnnotations = [];
    this.annotations.forEach((annotation) => {
      const imgBoxAnnotation = this.genbpolyRect(annotation);
      const { rect, selectionRect, transformer } = imgBoxAnnotation;

      // add to layers
      this.imgBoxAnnotations.push(imgBoxAnnotation);
      this.layer.add(rect);
      this.layer.add(selectionRect);
      this.layer.add(transformer);
    });

    if (this.errors) {
      this.errors.forEach((annotation) => {
        const { boundingPoly } = annotation;
        if (boundingPoly) {
          const vertices = boundingPoly.vertices;
          const rect = new Konva.Rect({
            x: vertices[1].x - 12,
            y: vertices[1].y - 5,
            width: 12,
            height: 5,
            fill: 'red',
          });

          var labelLeft = new Konva.Label({
            x: vertices[1].x + 1,
            y: (vertices[0].y + vertices[2].y) / 2,
            // opacity: 0.75,
          });

          labelLeft.add(
            new Konva.Tag({
              fill: 'black',
              pointerDirection: 'left',
              pointerWidth: 0,
              pointerHeight: 0,
              lineJoin: 'round',
            })
          );

          labelLeft.add(
            new Konva.Text({
              text: annotation.expected ? annotation.expected : '',
              fontFamily: 'Times New Roman',
              fontSize: 10,
              padding: 0,
              fill: 'yellow',
            })
          );

          this.layer.add(rect);
          this.layer.add(labelLeft);
        }
      }); 
    }

    if (this.annotations.length === 0) {
      this.fitStageIntoParentContainer();
      this.cdRef.detectChanges();
      return;
    }


    this.stage.on('mousemove', (e) => {
    });

    this.stage.on('click', (e) => {
      if (e.target.getClassName() !== 'Rect' && e.target.getClassName() !== 'Line') {
        this.undoButton();
      } else {
        this.xaxisLineClick = true;
        this.yaxisLineClick = true;
      }
    });

    if (fixSuggestions.length > 0) {
      this.xaxisSplitter = true;
      for (let item of fixSuggestions) {
        const { y } = item;
        this.xaxisLine = new Konva.Line({
          points: [0, y, 1500, y],
          stroke: 'orange',
          strokeWidth: 1,
          lineCap: 'round',
          lineJoin: 'round',
          name: 'horizontalLine',
          type: 'fixSuggestion'
          // zIndex: 2
        });
        this.layer.add(this.xaxisLine).draw();
      }
    }


    this.fitStageIntoParentContainer();
    this.cdRef.detectChanges();
  }

  genbpolyRect(annotation) {
    const { boundingPoly, tableIndex, isWordBlock } = annotation;
    const vertices = boundingPoly.vertices;
    const width = vertices[1].x - vertices[0].x;
    const height = vertices[2].y - vertices[0].y;

    // main bpoly rect
    const rect = new Konva.Rect({
      x: vertices[0].x,
      y: vertices[0].y - 5,
      width,
      height,
      stroke: isWordBlock ? null : annotation.error ? 'red' : 'lightblue',
      // strokeWidth: DEFAULT_STROKE_WIDTH,
      draggable: (!this.xaxisSplitter && !this.yaxisSplitter) ? true : false,
    });
    rect.on('click', (event) => {
      if (this.xaxisSplitter || this.yaxisSplitter) {
        return;
      }
      // this.logger.debug('click event: ', event);
      let xMin = (event.evt as any).layerX;
      const yMin = (event.evt as any).layerY;
      const konvaEl = document.getElementById('konva-stage-parent');
      if (konvaEl.offsetWidth - xMin < 200) {
        xMin = konvaEl.offsetWidth / 2;
      }
      // display text popover
      const popoverEl = document.getElementById('ann-popover');
      popoverEl.style.top = `${yMin}px`;
      popoverEl.style.left = `${xMin}px`;
      popoverEl.style.position = 'absolute';
      this.showAnnotationPopover = true;

      this.selectedAnnotation = annotation;
    });
    rect.on('mouseover', (event) => {
      document.body.style.cursor = 'pointer';
      if (this.xaxisSplitter || this.yaxisSplitter || this.showWordBlocks) {
        return;
      }
      this.response.emit({
        eventType: 'mouseover',
        event,
        tableBounds: this.tableBounds[tableIndex],
        rectPos: [
          {
            x: rect.attrs.x, y: rect.attrs.y,
          },
          {
            x: rect.attrs.x + rect.attrs.width, y: rect.attrs.y,
          },
          {
            x: rect.attrs.x + rect.attrs.width, y: rect.attrs.y + height,
          },
          {
            x: rect.attrs.x, y: rect.attrs.y + height,
          },
        ],
      });
    });
    rect.on('mouseout', (event) =>  {
      document.body.style.cursor = 'default';
      if (this.xaxisLine) {
        this.xaxisLine.destroy();
      }
      if (this.yaxisLine) {
        this.yaxisLine.destroy();
      }
    });
    rect.on('mousemove', (event) => {
      const f = this.stage.getPointerPosition();
      if (this.xaxisLine && !this.xaxisLineClick && this.xaxisLine.attrs.type !== 'fixSuggestion') {
        this.xaxisLine.destroy();
      }
      if (this.yaxisLine && !this.yaxisLineClick) {
        this.yaxisLine.destroy();
      }
      this.xaxisLineClick = false;
      this.yaxisLineClick = false;

      const relY = (f.y / this.stage.height()) * this.image.getHeight();
      if (!this.showWordBlocks && this.tableBounds.length > 0) {
        this.xaxisLine = new Konva.Line({
          points: [this.tableBounds[tableIndex].xLeftArr[0], relY,
          this.tableBounds[tableIndex].xRightArr[this.tableBounds[tableIndex].xRightArr.length - 1], relY],
          stroke: 'red',
          strokeWidth: 1,
          lineCap: 'round',
          lineJoin: 'round',
          name: 'horizontalLine',
          // zIndex: 2
        });
      }

      const relX = (f.x / this.stage.width()) * this.image.getWidth();
      if (!this.showWordBlocks && this.tableBounds.length > 0) {
        const topY = this.tableBounds[tableIndex].yTopArr[0] - 5 > 0 ?
          this.tableBounds[tableIndex].yTopArr[0] - 5 : this.tableBounds[tableIndex].yTopArr[0] - 5;
        this.yaxisLine = new Konva.Line({
          points: [relX, topY, relX, this.tableBounds[tableIndex].yBottomArr[this.tableBounds[tableIndex].yBottomArr.length - 1]],
          stroke: 'red',
          strokeWidth: 1,
          lineCap: 'round',
          lineJoin: 'round',
          name: 'verticalLine',
          // zIndex: 2
        });
      }

      if (this.xaxisSplitter && !this.showWordBlocks) {
        this.layer.add(this.xaxisLine).draw();
      } else if (this.yaxisSplitter && !this.showWordBlocks) {
        this.layer.add(this.yaxisLine).draw();
      }
    });

    // selection rect
    const selectionRect = new Konva.Rect({
      fill: 'rgba(0,0,255,0.5)',
      visible: true,
    });
    const tr = new Konva.Transformer({
      rotateEnabled: false,
      borderStroke: isWordBlock ? null : '#008000',
      anchorSize: DEFAULT_ACHOR_SIZE,
      resizeEnabled: false,
    });
    tr.nodes([rect]);

    const updateVertices = () => {
     // const clientRect = rect.getSelfRect();
      const x = rect.x();
      const y = rect.y();
      const rectWidth = rect.width() * rect.scaleX();
      const rectHeight = rect.height() * rect.scaleY();
      // this.logger.debug('clientRect: ', clientRect);

      annotation.vertices = [
        { x, y },
        { x: x + rectWidth, y },
        { x: x + rectWidth, y: y + rectHeight },
        { x, y: y + rectHeight },
      ] as Vertex[];
    };

    // transform events
    rect.on('transformstart', () => {
      // this.logger.log('transform start');
      updateVertices();
      this.cdRef.detectChanges();
    });
    rect.on('dragmove', () => {
      this.xaxisSplitter = false;
      this.yaxisSplitter = false;
      this.clearLine('B');
      if (this.showWordBlocks) {

        if (this.xaxisLine) {
          this.xaxisLine.destroy();
        }
        if (this.yaxisLine) {
          this.yaxisLine.destroy();
        }
        this.xaxisLine = new Konva.Line({
          points: [annotation.boundingPoly.vertices[0].x, annotation.boundingPoly.vertices[0].y - 5,
                   rect.attrs.x, annotation.boundingPoly.vertices[0].y - 5],
          stroke: 'red',
          strokeWidth: 1,
          lineCap: 'round',
          lineJoin: 'round',
          name: 'horizontalLine',
          // zIndex: 2
        });
        this.layer.add(this.xaxisLine).draw();

        this.xaxisLine = new Konva.Line({
          points: [annotation.boundingPoly.vertices[0].x, rect.attrs.y, rect.attrs.x, rect.attrs.y],
          stroke: 'red',
          strokeWidth: 1,
          lineCap: 'round',
          lineJoin: 'round',
          name: 'horizontalLine',
          // zIndex: 2
        });
        this.layer.add(this.xaxisLine).draw();

        this.yaxisLine = new Konva.Line({
          points: [annotation.boundingPoly.vertices[0].x, annotation.boundingPoly.vertices[0].y,
                   annotation.boundingPoly.vertices[0].x, rect.attrs.y],
          stroke: 'red',
          strokeWidth: 1,
          lineCap: 'round',
          lineJoin: 'round',
          name: 'verticalLine',
          // zIndex: 2
        });
        this.layer.add(this.yaxisLine).draw();

        this.yaxisLine = new Konva.Line({
          points: [rect.attrs.x, annotation.boundingPoly.vertices[0].y, rect.attrs.x, rect.attrs.y],
          stroke: 'red',
          strokeWidth: 1,
          lineCap: 'round',
          lineJoin: 'round',
          name: 'verticalLine',
          // zIndex: 2
        });
        this.layer.add(this.yaxisLine).draw();
      }
      // this.logger.debug('drag move event for annotation: ', annotation);
      updateVertices();
      this.cdRef.detectChanges();
    });
    rect.on('transform', (event) => {
      // this.logger.debug('transform event for annotation: ', event);
      updateVertices();
      this.cdRef.detectChanges();
    });
    rect.on('transformend', () => {
      // this.logger.log('transform end');
      updateVertices();
      this.cdRef.detectChanges();
    });
    rect.on('dragend', (event) => {
      this.xaxisSplitter = false;
      this.yaxisSplitter = false;
      this.clearLine('B');
      if (this.xaxisSplitter || this.yaxisSplitter) {
        return;
      }
      this.selectedAnnotation = annotation;
      this.response.emit({
        eventType: 'dragend',
        event,
        rect: rect,
        selectedAnnotation: this.selectedAnnotation,
      });
      // this.logger.debug('drag move event for annotation: ', annotation);
      // updateVertices();
      this.cdRef.detectChanges();
    });

    return {
      rect,
      selectionRect,
      transformer: tr,
      annotation,
    } as ImageBoxAnnotation;
  }

  removeAnnotation(annotation: ImageAnnotation) {
    const imgBoxAnnotation = this.imgBoxAnnotations.find(
      (a) => a.annotation === annotation,
    );
    if (annotation === this.selectedAnnotation) {
      this.selectedAnnotation = null;
      this.showAnnotationCode = false;
      this.showAnnotationPopover = false;
    }
    // this.logger.debug('deleting imgBoxAnnotation: ', imgBoxAnnotation);

    const { rect, selectionRect, transformer } = imgBoxAnnotation;
    this.annotations = this.annotations.filter(
      (a) => a !== imgBoxAnnotation.annotation,
    );

    // remove from layer
    rect.destroy();
    selectionRect.destroy();
    transformer.destroy();
  }

  removeSelectedAnnotation(deleteType) {
    this.showAnnotationCode = false;
    this.showAnnotationPopover = false;

    const imgBoxAnnotation = this.imgBoxAnnotations.find(
      (a) => a.annotation === this.selectedAnnotation,
    );
    // this.logger.debug('deleting imgBoxAnnotation: ', imgBoxAnnotation);

    const { rect, selectionRect, transformer } = imgBoxAnnotation;
    this.annotations = this.annotations.filter(
      (a) => a !== imgBoxAnnotation.annotation,
    );

    // remove from layer
    rect.destroy();
    selectionRect.destroy();
    transformer.destroy();
    const selectedCell = this.selectedAnnotation.cell;
    this.selectedAnnotation = null;

    this.response.emit({
      eventType: 'delete',
      selectedCell: selectedCell,
      deleteType,
    });
  }

  addAnnotation(vertices?: Vertex[]) {
    const newAnnotation = {
      vertices: vertices ?? DEFAULT_NEW_BPOLY_VERTEX,
      description: 'New Annotation',
    } as ImageAnnotation;
    const imgBoxAnnotation = this.genbpolyRect(newAnnotation);
    const { rect, selectionRect, transformer } = imgBoxAnnotation;

    // add to layers
    this.imgBoxAnnotations.push(imgBoxAnnotation);
    this.annotations.unshift(newAnnotation);
    this.layer.add(rect);
    this.layer.add(selectionRect);
    this.layer.add(transformer);
    this.cdRef.detectChanges();
  }

  updateAnnotation (newValue) {
    this.showAnnotationPopover = false;
    for (let i = 0; i < this.annotations.length; i++) {
      if (this.annotations[i].cell.id === this.selectedAnnotation.cell.id) {
        if (this.annotations[i].description !== newValue) {
          this.annotations[i].description = newValue;
          this.response.emit({
            eventType: 'update',
            newValue: newValue,
            cell: this.selectedAnnotation.cell,
          });
          break;
        }
      }
    }
  }

  clearLine(direction) {
    let filterCondition;
    switch (direction) {
        case 'B':
            filterCondition = (item) => !item.attrs.lineCap;
            break;
        case 'V':
            filterCondition = (item) => !item.attrs.lineCap || (item.attrs.lineCap && item.attrs.name !== 'verticalLine');
            break;
        case 'H':
            filterCondition = (item) => !item.attrs.lineCap || (item.attrs.lineCap && item.attrs.name !== 'horizontalLine');
            break;
        default:
            return;
    }
    this.layer.children = this.layer.children.filter(filterCondition);
 }

  horizontalButton () {
    this.clearLine('V');
    this.xaxisSplitter = true;
    this.yaxisSplitter = false;
    if (this.yaxisLine) {
      this.yaxisLine.destroy();
    }
  }

  verticalButton () {
    this.clearLine('H');
    this.xaxisSplitter = false;
    this.yaxisSplitter = true;
    if (this.xaxisLine) {
      this.xaxisLine.destroy();
    }
  }

  undoButton () {
    this.clearLine('B');
    this.xaxisSplitter = false;
    this.yaxisSplitter = false;

    this.response.emit({
      eventType: 'reinit',
    });
  }

  proceedButton() {
    let eventType;
    const linePos = [];
    
    const filterLines = (type) => {
      this.layer.children.forEach((item) => {
        if (item.attrs.name && item.attrs.name === type + 'Line') {
          linePos.push({ x: item.attrs.points[0], y: item.attrs.points[1] });
        }
      });
    };
  
    if (this.xaxisSplitter) {
      eventType = 'horizontalLine';
      filterLines('horizontal');
    } else if (this.yaxisSplitter) {
      eventType = 'verticalLine';
      filterLines('vertical');
    }
  
    this.xaxisSplitter = false;
    this.yaxisSplitter = false;
    this.clearLine('B');
    
    if (this.yaxisLine) {
      this.yaxisLine.destroy();
    }
    if (this.xaxisLine) {
      this.xaxisLine.destroy();
    }
  
    this.response.emit({
      eventType: eventType,
      linePos: linePos,
    });
  }
}
